import { formatCurrency } from '@/components/molecules/financialImpact';
import type { Invoice, InvoiceStatus } from '@/types/invoice';

type SettlingPayment = NonNullable<Invoice['settlingPayment']>;

export interface InvoiceNextStepInput {
  status: InvoiceStatus;
  dueDate?: string | null;
  balanceDue?: number;
  /**
   * The payment the settling banner above the next step is showing, or null
   * when the banner is not shown. Pass the same value the banner renders from,
   * so the two cannot disagree about whether money is in flight.
   */
  settlingPayment?: Pick<
    SettlingPayment,
    'amount' | 'rail' | 'inFlightTotal' | 'inTransitTotal' | 'balanceCoverageTotal'
  > | null;
  financialActionsHidden?: boolean;
  /**
   * A settled refund went back on a payment this invoice was paid with, so at
   * least part of the balance is money returned to the customer rather than a
   * bill they have ignored.
   */
  reopenedByRefund?: boolean;
  /**
   * Bank money still clearing that was already applied to the invoice, such as
   * an ACH collected on the contact page and spent as credit. It is inside the
   * stored paid figure, so the stored status can read paid before the bank
   * settles. Null for a viewer money in flight is withheld from.
   */
  provisionalClearingTotal?: number | null;
  formatDate: (iso: string) => string;
}

/**
 * Dollars in flight across the invoice's own settling debits. The API names one
 * debit and totals all of them; an API old enough to omit the total falls back
 * to the one debit, which can only understate what is covered.
 */
function settlingOwnInFlightTotal(
  settlingPayment: Pick<SettlingPayment, 'amount' | 'inFlightTotal'>,
): number {
  const { amount, inFlightTotal } = settlingPayment;
  return typeof inFlightTotal === 'number' &&
    Number.isFinite(inFlightTotal) &&
    inFlightTotal >= amount
    ? inFlightTotal
    : amount;
}

/**
 * Dollars in transit toward the invoice: its own settling debits, job money
 * that will land on it, and clearing money already applied to it. The figure
 * to name. An API that omits `inTransitTotal` falls back to the own debits.
 */
export function settlingInFlightTotal(
  settlingPayment: Pick<SettlingPayment, 'amount' | 'inFlightTotal' | 'inTransitTotal'>,
): number {
  const own = settlingOwnInFlightTotal(settlingPayment);
  const { inTransitTotal } = settlingPayment;
  return typeof inTransitTotal === 'number' &&
    Number.isFinite(inTransitTotal) &&
    inTransitTotal >= own
    ? inTransitTotal
    : own;
}

/**
 * True when more than one payment is in flight on this invoice.
 *
 * `rail`, `provider`, `since` and `paymentId` all describe the OLDEST debit
 * only, while the amount shown is the total. With several in flight those can
 * disagree about kind as well as size — a $300 bank debit plus a $200 pending
 * card charge is one bank row and one ambiguous card row — so every surface
 * that shows the total has to stop claiming the oldest row's rail. Derived from
 * the figures themselves rather than a separate count, so it cannot drift from
 * the total it qualifies.
 */
export function settlingHasSeveralInFlight(
  settlingPayment: Pick<SettlingPayment, 'amount' | 'inFlightTotal' | 'inTransitTotal'>,
): boolean {
  return settlingInFlightTotal(settlingPayment) > settlingPayment.amount + 0.005;
}

/**
 * What is still owed once the money in flight lands, or null when it covers the
 * balance. Mirrors the dunning hold (`holdsChaseForSettlement` in operations):
 * every settling debit counts, a cent of drift between the gateway amounts and
 * the recomputed balance still counts as covered, and a balance that cannot be
 * read counts as covered rather than asking for a figure we could not compute.
 * The settling banner and the next step both read this, so they cannot disagree
 * about whether to chase.
 */
export function settlingPaymentShortfall(
  balanceDue: number | null | undefined,
  settlingPayment: Pick<SettlingPayment, 'amount' | 'inFlightTotal' | 'balanceCoverageTotal'>,
): number | null {
  if (typeof balanceDue !== 'number' || !Number.isFinite(balanceDue)) return null;
  // Measured against the invoice's own debits and the job money that will land
  // on it, never `inTransitTotal`: clearing money already applied to the
  // invoice is netted in `balanceDue` already, and counting it again would call
  // a balance covered that is still owed.
  const cashInFlight = settlingOwnInFlightTotal(settlingPayment);
  const coverage = settlingPayment.balanceCoverageTotal;
  const inFlight =
    typeof coverage === 'number' && Number.isFinite(coverage) && coverage >= cashInFlight
      ? coverage
      : cashInFlight;
  return inFlight + 0.01 >= balanceDue ? null : balanceDue - inFlight;
}

/**
 * Copy for an invoice with a payment in flight, or null to fall through to the
 * ordinary status copy. Only statuses that are still collecting are affected.
 */
function getSettlingNextStepCopy(
  invoice: InvoiceNextStepInput,
  settlingPayment: Pick<
    SettlingPayment,
    'amount' | 'rail' | 'inFlightTotal' | 'inTransitTotal' | 'balanceCoverageTotal'
  >,
): string | null {
  const { status } = invoice;
  const collecting =
    status === 'open' ||
    status === 'sent' ||
    status === 'viewed' ||
    status === 'partial' ||
    status === 'overdue';
  if (!collecting) return null;

  // Same distinction the banner draws: a pending bank debit is clearing
  // normally, a pending card charge is an outcome still being verified.
  const isBank = settlingPayment.rail === 'BANK';
  const total = settlingInFlightTotal(settlingPayment);
  // More than one debit in flight. The rail is the oldest debit's, so the
  // several-payment copy does not claim they all share it.
  const several = settlingHasSeveralInFlight(settlingPayment);

  // Money in flight only justifies "don't chase" when it covers what is owed.
  const shortfall = settlingPaymentShortfall(invoice.balanceDue, settlingPayment);
  if (shortfall === null) {
    if (several) {
      return "Payments covering the balance are in flight. Don't collect again unless one is returned or fails.";
    }
    return isBank
      ? 'A pending ACH payment covers the balance. No action needed unless it is returned.'
      : "A card payment covering the balance is awaiting confirmation. Don't collect again until it resolves.";
  }

  // A partial payment in flight leaves the rest genuinely owed, so the chase
  // stays — but against the amount that is actually outstanding, since the
  // stored balance this reads still includes the money on its way (the
  // screens show it netted, this step works from the stored figure).
  const remaining = formatCurrency(shortfall);
  const inFlight = several
    ? `the ${formatCurrency(total)} in payments still in flight`
    : `the ${formatCurrency(settlingPayment.amount)} ${
        isBank ? 'pending ACH payment' : 'card payment awaiting confirmation'
      }`;

  switch (status) {
    case 'partial':
      return `Partially paid — ${remaining} still outstanding after ${inFlight}. Record the remaining balance or send a reminder.`;
    case 'overdue':
      return invoice.dueDate
        ? `${remaining} past due since ${invoice.formatDate(invoice.dueDate)} after ${inFlight}. Send a reminder, share a payment link, or apply a late fee.`
        : `${remaining} past due after ${inFlight}. Send a reminder, share a payment link, or apply a late fee.`;
    default:
      return invoice.dueDate
        ? `${remaining} still outstanding after ${inFlight} — due ${invoice.formatDate(invoice.dueDate)}. Send a reminder if it lingers.`
        : `${remaining} still outstanding after ${inFlight}. Send a reminder if it has been a few days.`;
  }
}

export function getInvoiceNextStepCopy(invoice: InvoiceNextStepInput): string {
  if (invoice.financialActionsHidden) {
    switch (invoice.status) {
      case 'draft':
        return 'This invoice is still a draft.';
      case 'open':
      case 'sent':
      case 'viewed':
        return invoice.dueDate
          ? `Awaiting payment — due ${invoice.formatDate(invoice.dueDate)}.`
          : 'Awaiting payment.';
      case 'partial':
        return 'This invoice is partially paid.';
      case 'overdue':
        return invoice.dueDate
          ? `Past due since ${invoice.formatDate(invoice.dueDate)}.`
          : 'This invoice is past due.';
      case 'paid':
        return 'This invoice is paid in full.';
      case 'void':
        return 'This invoice was voided and can no longer be paid.';
      case 'written_off':
        return 'This invoice was written off.';
      default:
        return '';
    }
  }

  if (invoice.settlingPayment) {
    const settlingCopy = getSettlingNextStepCopy(invoice, invoice.settlingPayment);
    if (settlingCopy !== null) return settlingCopy;
  }

  // A refund reopens the invoice by what went back, so the balance reads as
  // owed — but it is money the business returned, not an unpaid bill. Chasing
  // the customer for it would ask them to pay back their own refund.
  if (
    invoice.reopenedByRefund &&
    (invoice.status === 'open' ||
      invoice.status === 'sent' ||
      invoice.status === 'viewed' ||
      invoice.status === 'partial' ||
      invoice.status === 'overdue')
  ) {
    const reopened =
      typeof invoice.balanceDue === 'number' && Number.isFinite(invoice.balanceDue)
        ? `A refund reopened this invoice — ${formatCurrency(invoice.balanceDue)} is open again because money went back to the customer.`
        : 'A refund reopened this invoice because money went back to the customer.';
    return `${reopened} No reminder is needed unless they still owe it.`;
  }

  switch (invoice.status) {
    case 'draft':
      return 'Send to the customer to start collecting payment.';
    case 'open':
    case 'sent':
    case 'viewed':
      return invoice.dueDate
        ? `Awaiting payment — due ${invoice.formatDate(invoice.dueDate)}. Send a reminder if it lingers.`
        : 'Awaiting payment. Send a reminder if it has been a few days.';
    case 'partial':
      return 'Partially paid. Record the remaining balance or send a reminder.';
    case 'overdue':
      return invoice.dueDate
        ? `Past due since ${invoice.formatDate(invoice.dueDate)}. Send a reminder, share a payment link, or apply a late fee.`
        : 'Past due. Send a reminder, share a payment link, or apply a late fee.';
    case 'paid':
      // Paid with bank money that has not cleared (a contact-page collect spent
      // as credit): the invoice reads Clearing, so the next step says so, the
      // same words as a bank payment made on the invoice itself.
      if ((invoice.provisionalClearingTotal ?? 0) > 0) {
        return 'A pending ACH payment covers the balance. No action needed unless it is returned.';
      }
      return 'Paid in full. Download the receipt or PDF for your records.';
    case 'void':
      return 'This invoice was voided and can no longer be paid.';
    case 'written_off':
      return 'This invoice was written off.';
    default:
      return '';
  }
}
